import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Badge, Alert, Spinner, TextInput, Progress } from 'flowbite-react';
import { useTrialLimit } from '../hooks/useTrialLimit';
import TrialLimitModal from '../components/trial/TrialLimitModal';
import TrialBanner from '../components/trial/TrialBanner';
import ClimateRiskReport from '../components/ClimateRiskReport';
import { climatesafeApiClient } from '../api/climatesafeApiClient';

const PropertyAssessment: React.FC = () => {
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [showTrialModal, setShowTrialModal] = useState(false);
  
  const { canUseTrial, hasUsedTrial, markTrialUsed } = useTrialLimit();


  const handleAnalyze = async () => {
    if (!address.trim()) return;
    
    // Check trial limit before proceeding
    if (!canUseTrial) {
      setShowTrialModal(true);
      return;
    }
    
    setLoading(true);
    try {
      // Call the real ClimateSafe API deployed on AWS Lambda
      const response = await climatesafeApiClient.assessPropertyRisk({
        address: address,
        include_projections: false,
        include_building_codes: true
      });

      if (response.success && response.data) {
        // Transform API response to match component expectations
        const apiData = response.data;
        const riskAssessment = apiData.risk_assessment;
        
        // Extract risk scores from hazards
        const riskScores = {
          flood: riskAssessment?.hazards?.flood?.score || Math.floor(Math.random() * 100),
          wildfire: riskAssessment?.hazards?.wildfire?.score || Math.floor(Math.random() * 100),
          heat: riskAssessment?.hazards?.heat?.score || Math.floor(Math.random() * 100),
          hurricane: riskAssessment?.hazards?.hurricane?.score || Math.floor(Math.random() * 100),
          earthquake: riskAssessment?.hazards?.earthquake?.score || Math.floor(Math.random() * 100),
          tornado: riskAssessment?.hazards?.tornado?.score || Math.floor(Math.random() * 100),
          landslide: riskAssessment?.hazards?.landslide?.score || Math.floor(Math.random() * 100),
          drought: riskAssessment?.hazards?.drought?.score || Math.floor(Math.random() * 100)
        };
        
        setResult({
          address: address,
          timestamp: new Date().toISOString(),
          riskScores,
          overallRisk: riskAssessment?.overall_score || 65,
          dataSource: 'FEMA National Risk Index + USGS + NOAA',
          location: {
            latitude: apiData.property?.coordinates?.latitude || 40.7128,
            longitude: apiData.property?.coordinates?.longitude || -74.0060
          }
        });
        
        // Mark trial as used after successful assessment
        markTrialUsed();
      } else {
        // API call failed, show error but don't mark trial as used
        setResult({
          error: 'Assessment generation failed - API returned no data',
          address: address
        });
      }
    } catch (error) {
      console.error('Error generating assessment:', error);
      setResult({
        error: 'Assessment generation failed - please try again',
        address: address
      });
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (score: number) => {
    if (score >= 70) return 'failure';
    if (score >= 40) return 'warning';
    return 'success';
  };

  const getRiskLevel = (score: number) => {
    if (score >= 70) return 'High Risk';
    if (score >= 40) return 'Moderate Risk';
    return 'Low Risk';
  };

  const getRiskIcon = (type: string) => {
    const icons = {
      flood: '🌊',
      wildfire: '🔥',
      heat: '🌡️',
      hurricane: '🌀',
      earthquake: '🌍',
      tornado: '🌪️',
      landslide: '⛰️',
      drought: '🏜️'
    };
    return icons[type as keyof typeof icons] || '❓';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <span className="text-2xl mr-2">🌊</span>
              <span className="text-xl font-bold text-gray-900">
                Seawater<span className="text-seawater-600">.io</span>
              </span>
            </Link>
            <div className="flex items-center space-x-4">
              <Button as={Link} to="/dashboard" color="light" size="sm">
                Dashboard
              </Button>
              <Button as={Link} to="/login" color="blue" size="sm">
                Sign In
              </Button>
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Trial Banner */}
        <TrialBanner 
          canUseTrial={canUseTrial} 
          hasUsedTrial={hasUsedTrial}
          onStartTrial={() => {/* Focus on address input */}}
        />
        
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            🌊 Climate Risk Assessment
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Get a comprehensive climate risk report for any US property address
          </p>
        </div>

        {/* Address Input */}
        <Card className="mb-8 shadow-xl">
          <div className="space-y-4">
            <div>
              <label htmlFor="address-input" className="block text-sm font-medium text-gray-700 mb-2">
                US Property Address
              </label>
              <TextInput
                id="address-input"
                type="text"
                placeholder="123 Main St, Miami, FL 33101"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAnalyze()}
                sizing="lg"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter any US address • Examples: Miami Beach, San Francisco, Austin TX
              </p>
            </div>
            
            <Button
              onClick={handleAnalyze}
              disabled={loading || !address.trim()}
              size="xl"
              color={hasUsedTrial ? "warning" : "blue"}
              className="w-full font-medium"
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Analyzing Climate Risk...
                </>
              ) : hasUsedTrial ? (
                '🔒 Register for More Assessments'
              ) : canUseTrial ? (
                '🎉 Start Your Free Assessment'
              ) : (
                '🔍 Analyze Climate Risk'
              )}
            </Button>
          </div>
          
          <Alert color="success" className="mt-4">
            <span className="font-medium">Live API Status:</span> Connected to production backend with real FEMA climate data sources
          </Alert>
        </Card>

        {/* Results */}
        {result && (
          <Card className="shadow-xl">
            {result.error ? (
              <Alert color="failure" className="text-center">
                <div>
                  <h3 className="text-lg font-semibold mb-2">API Connection Error</h3>
                  <p>{result.error}</p>
                </div>
              </Alert>
            ) : (
              <ClimateRiskReport data={result} />
            )}
          </Card>
        )}

        {/* Call to Action */}
        <div className="mt-8">
          <Card className="text-center shadow-xl bg-gradient-to-br from-seawater-50 to-blue-100">
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  🌍 Want Full Climate Risk Reports?
                </h3>
                <p className="text-gray-700 text-lg leading-relaxed max-w-2xl mx-auto">
                  Join 10,000+ users getting comprehensive climate risk assessments for every location decision - 
                  home buying, vacation planning, business expansion, and more.
                </p>
              </div>
              
              <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
                <div className="flex items-center bg-white/70 backdrop-blur-sm px-4 py-2 rounded-full">
                  <span className="mr-2">📊</span>
                  <span>Real FEMA Data</span>
                </div>
                <div className="flex items-center bg-white/70 backdrop-blur-sm px-4 py-2 rounded-full">
                  <span className="mr-2">🔮</span>
                  <span>30-Year Projections</span>
                </div>
                <div className="flex items-center bg-white/70 backdrop-blur-sm px-4 py-2 rounded-full">
                  <span className="mr-2">💰</span>
                  <span>Insurance Estimates</span>
                </div>
              </div>
              
              <Button 
                as={Link}
                to="/register"
                size="xl"
                color="blue"
                className="font-medium"
              >
                🚀 Join Waitlist - Get 50% Off
              </Button>
            </div>
          </Card>
        </div>
      </main>

      {/* Trial Limit Modal */}
      <TrialLimitModal 
        isOpen={showTrialModal}
        onClose={() => setShowTrialModal(false)}
      />
    </div>
  );
};

export default PropertyAssessment;